<template>
  <button
    class="collapse-content w-100 text-dark-3 ps-0"
    :aria-expanded="!collapsed"
    :aria-controls="componentId"
    @click="toggleCollapse"
  >
    <div class="w-100 px-0 d-flex flex-row justify-content-between align-items-center mb-space-xs">
      <h3 class="text-left" :class="titleClass">{{ title }}</h3>
      <font-awesome-icon
        class="ms-space-xxs"
        :icon="collapsed ? 'caret-up' : 'caret-down'"
      ></font-awesome-icon>
    </div>
  </button>
  <Transition
    name="accordion"
    @before-enter="handleCollapseShown"
    @enter="handleCollapseShown"
    @before-leave="handleCollapseHidden"
    @leave="handleCollapseHidden"
  >
    <div v-show="collapsed" :id="componentId" :aria-hidden="collapsed">
      <div v-for="option in options" :key="option.value" class="form-check mb-space-xxs">
        <label
          class="form-check-label fs-medium d-inline-flex align-items-center"
          :for="`${componentId}-${option.value}`"
          >{{ option.text }}
        </label>
        <input
          :id="`${componentId}-${option.value}`"
          class="form-check-input"
          type="checkbox"
          :checked="selected?.has(option.value)"
          :aria-checked="selected?.has(option.value)"
          :aria-label="option.text"
          @click="handleChecked(option)"
        />
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import type { TitleVariant, VariantOption } from "~/types/types";
export type FilterCollapseOption = {
  value: string;
  text: string;
  description: string;
};

type Props = {
  componentId: string;
  title: string;
  options: FilterCollapseOption[];
  showTooltip?: boolean;
  titleSize?: TitleVariant;
  titleVariant?: VariantOption;
};
const props = withDefaults(defineProps<Props>(), {
  showTooltip: false,
  titleSize: "small",
  titleVariant: "dark-3"
});

/*
 ********************************************
 *  Emits
 ********************************************
 */
const emits = defineEmits<{
  (e: "checked" | "unchecked" | "change", value: FilterCollapseOption): void;
  (e: "input", value: string[]): void;
  (e: "collapse:hidden" | "collapse:shown"): void;
}>();
/*
 ********************************************
 *  data
 ********************************************
 */
const selected = defineModel<Set<string>>();
const collapsed = ref<boolean>(false);

/*
 ********************************************
 * computed
 ********************************************
 */
const titleClass = computed(() => {
  return `h3-${props.titleSize} fs-${props.titleVariant}`;
});
/*
 ********************************************
 *  methods
 ********************************************
 */
const toggleCollapse = () => {
  collapsed.value = !collapsed.value;
};
const handleChecked = (option: FilterCollapseOption) => {
  if (selected.value?.has(option.value)) {
    selected.value.delete(option.value);
    emits("unchecked", option);
  } else {
    selected.value?.add(option.value);
    emits("checked", option);
  }
  emits("change", option);
};

const initialLoad = ref(false);
const handleCollapseShown = (el: Element) => {
  if (!(el instanceof HTMLElement)) return;
  if (collapsed.value) {
    el.style.height = "0px";
    el.style.height = el.scrollHeight + "px";
    if ((el as any).__collapseShown) {
      emits("collapse:shown");
    }
    (el as any).__collapseShown = true;
  } else if (initialLoad.value) {
    el.style.height = el.scrollHeight + "px";
    setTimeout(() => {
      el.style.height = "0px";
    }, 10);
  }
  initialLoad.value = true;
};

const handleCollapseHidden = (el: HTMLElement | Element) => {
  if (!(el instanceof HTMLElement)) return;
  el.style.height = "0";
  if (!collapsed.value) {
    if (!(el as any).__collapseShown) {
      emits("collapse:hidden");
    }
    (el as any).__collapseShown = false;
  }
};
</script>

<style scoped>
.collapse-content {
  appearance: none;
  background-color: transparent;
  border: none;
  cursor: pointer;
}
.accordion-enter-active,
.accordion-leave-active {
  will-change: height, opacity;
  transition: height 0.2s ease;
  overflow: hidden;
}
</style>
